/**
 * Chats and Calls, at the foot of the sidebar as two tiles rather than two
 * more rows.
 *
 * They were rows, then they went into the gold corner button, and Coleman
 * found that less obvious than either (2026-10-07). They aren't places you
 * browse, they're things that change: a call coming up, a message waiting.
 * So each tile carries its live fact (when the next call is, how many
 * messages are unread) and is otherwise out of the way of the nav above it.
 *
 * My Organization and the Intern Hub sit above them as the same kind of tile
 * (Coleman, 2026-10-17), and only for the people they belong to: members of
 * a consulting org, and whoever hubVisible lets into the hub. Each takes a
 * full row, because "My Organization" doesn't fit in half of one.
 */
import React from "react";
import { CalendarDays, MessageCircle, Building2, GraduationCap } from "lucide-react";
import { GOLD } from "../constants/theme";

/** "Call tomorrow at 6:00 AM" → "Tomorrow at 6:00 AM"; null stays null. */
export function shortCallLine(line) {
  if (!line || typeof line !== "string") return null;
  const s = line.replace(/^Call /, "").replace(/^Next call /, "");
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function Tile({ Icon, label, fact, hot, badge, active, onClick }) {
  return (
    <button type="button" className="btn sb-live-tile" onClick={onClick} aria-current={active ? "page" : undefined}
      style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 4,
        padding: "10px 11px", borderRadius: 10, cursor: "pointer", textAlign: "left", fontFamily: "inherit",
        background: active ? "rgba(226,221,159,0.08)" : "rgba(255,255,255,0.025)",
        border: `1px solid ${active ? "rgba(226,221,159,0.35)" : "rgba(255,255,255,0.05)"}`, position: "relative" }}>
      <span style={{ display: "flex", alignItems: "center", gap: 7, width: "100%" }}>
        <Icon size={14} color={hot || active ? GOLD : "#777"}/>
        <span style={{ fontSize: 12, fontWeight: 700, color: active ? "#F5F5F5" : "#B5B2A8" }}>{label}</span>
        {badge > 0 && (
          <span aria-label={`${badge} unread`} style={{ marginLeft: "auto", minWidth: 18, height: 18, borderRadius: 9,
            padding: "0 5px", background: GOLD, color: "#0A0A0A", fontSize: 9.5, fontWeight: 800,
            display: "flex", alignItems: "center", justifyContent: "center" }}>
            {badge > 9 ? "9+" : badge}
          </span>
        )}
      </span>
      {fact && (
        <span style={{ fontSize: 11, lineHeight: 1.35, color: hot ? GOLD : "#6E6B63", fontWeight: hot ? 700 : 500,
          width: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{fact}</span>
      )}
    </button>
  );
}

const onRoute = (nav, id) => typeof nav === "string" && (nav === id || nav.startsWith(id + "/"));

/**
 * `org`: the org the My Organization tile opens ({ name }), or null for no
 * tile. `onOrg` opens it. `showInterns`: the Intern Hub tile.
 */
export function SidebarLive({ nav, go, callLine, chatUnread = 0, showChats = true, style, tourTarget,
  org = null, onOrg, showInterns = false }) {
  const call = shortCallLine(callLine);
  const callHot = !!call && /^Today/.test(call);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6, ...style }}>
    {org && (
      <div style={{ display: "flex" }}>
        <Tile Icon={Building2} label="My Organization" fact={org.name || null}
          active={onRoute(nav, "org")} onClick={onOrg || (() => go("org"))}/>
      </div>
    )}
    {showInterns && (
      <div style={{ display: "flex" }}>
        <Tile Icon={GraduationCap} label="Intern Hub"
          active={onRoute(nav, "interns")} onClick={() => go("interns")}/>
      </div>
    )}
    <div style={{ display: "flex", gap: 6 }}
      {...(tourTarget ? { "data-tour-target": tourTarget, "data-moved": "live", "data-moved-label": "Calls and Chats moved here" } : {})}>
      <Tile Icon={CalendarDays} label="Calls" fact={call || "See the schedule"} hot={callHot}
        active={nav === "calls"} onClick={() => go("calls")}/>
      {showChats && (
        <Tile Icon={MessageCircle} label="Chats" badge={chatUnread}
          fact={chatUnread > 0 ? `${chatUnread} unread` : "All caught up"}
          active={onRoute(nav, "chats")} onClick={() => go("chats")}/>
      )}
    </div>
    </div>
  );
}

export default SidebarLive;
